import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Box, Typography, Grid } from "@mui/material";
import { User } from "@/interfaces/User";
import { IconX } from "@tabler/icons-react";
import { Button } from "./ui/button";
import { ImagePlus } from "lucide-react";
import { useAppDispatch } from "@/redux/hooks";
import { getUser } from "@/redux/user/user.action";
import { Input } from "./ui/input";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { createFeedback } from "@/redux/feedback/feedback.action";
import { FeedbackRequest } from "@/redux/feedback/types/Feedback.interface";
import { SuccessToast } from "@/utilities/toasts/SuccessToast";
import { ErrorToast } from "@/utilities/toasts/ErrorToast";
import { RequestStatus } from "@/utilities/ResponseEnum/RequestStatus";
import { useSelector } from "react-redux";
import { feedbackSelector } from "@/redux/feedback/feedback.selector";

interface DialogProfileProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  userInfo: User;
}

interface UploadImage {
  image: File;
  url: string;
}

const FormSchema = z.object({
  type: z.string().min(1, {
    message: "Por favor, selecciona el tipo de problema.",
  }),
  description: z.string().min(1, {
    message: "Por favor, describe el problema con más detalle.",
  }),
  images: z.array(z.instanceof(File)).max(2, {
    message: "Puedes adjuntar hasta 2 imágenes como máximo.",
  }),
});

const DialogCreateFeedback = ({
  open,
  setOpen,
  userInfo,
}: DialogProfileProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedImages, setUploadedImages] = useState<UploadImage[]>([]);
  const dispatch = useAppDispatch();
  const { loading } = useSelector(feedbackSelector).ui;
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      type: "",
      description: "",
      images: [],
    },
  });

  useEffect(() => {
    dispatch(getUser());
  }, [dispatch]);

  const handleUploadImage = (image: File) => {
    const url = URL.createObjectURL(image);
    const newImages = [...uploadedImages, { image, url }];
    setUploadedImages(newImages);
    form.setValue(
      "images",
      newImages.map((img) => img.image),
    );
  };

  const handleDeleteImage = (url: string) => {
    setUploadedImages((prevImages) => {
      const newImages = prevImages.filter((image) => image.url !== url);

      form.setValue(
        "images",
        newImages.map((img) => img.image),
      );

      return newImages;
    });
  };
  const handleSubmit = async (data: z.infer<typeof FormSchema>) => {
    const feedback: FeedbackRequest = {
      type: data.type,
      message: data.description,
      isResolved: false,
      userId: userInfo.id,
    };
    const result = await dispatch(
      createFeedback({ feedback, images: data.images }),
    );
    if (result.meta.requestStatus === RequestStatus.Fulfilled) {
      SuccessToast({
        message: (
          <p className="font-semibold">
            ¡Tu reporte ha sido enviado exitosamente!
            <br />
            Gracias por ayudarnos a mejorar. Nos pondremos en contacto si
            necesitamos más información.
          </p>
        ),
      });
      resetFormAndImages();
      setOpen(false);
    } else {
      ErrorToast({
        message: (
          <p className="font-semibold">
            No pudimos enviar tu reporte en este momento.
            <br />
            Por favor, revisa tu conexión o inténtalo nuevamente más tarde.
          </p>
        ),
      });
    }
  };

  const types = [
    {
      label: "Error en la plataforma (funcionalidad, carga, etc.)",
      value: "Error en la plataforma",
    },
    {
      label: "Información desactualizada o incorrecta",
      value: "Información desactualizada",
    },
    {
      label: "Sugerencia de mejora",
      value: "Sugerencia",
    },
    {
      label: "Otro (especificar en el mensaje)",
      value: "Otro",
    },
  ];

  const resetFormAndImages = () => {
    form.reset();
    setUploadedImages([]);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={() => {
        resetFormAndImages();
        setOpen((prev) => !prev);
      }}
    >
      <VisuallyHidden>
        <DialogTitle>CreateFeedback</DialogTitle>
      </VisuallyHidden>
      <DialogContent
        className="bg-sidebar min-w-[15rem] w-[30rem]"
        aria-describedby={undefined}
      >
        <DialogHeader className="flex pb-0 h-fit">
          <Box className="flex justify-end w-full font-bold">
            <Button
              className="p-0 text-[#1F6B45] h-fit"
              onClick={() => {
                resetFormAndImages();
                setOpen(false);
              }}
            >
              <IconX className="p-0" />
            </Button>
          </Box>
          <DialogTitle>
            <Typography
              fontFamily={"Inter"}
              fontSize={"18px"}
              fontWeight={500}
              letterSpacing={0}
            >
              Reporta un problema
            </Typography>
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            ¿Tienes algún inconveniente, sugerencia o comentario? Cuéntanos con
            el mayor detalle posible para que podamos ayudarte rápidamente.
          </DialogDescription>
        </DialogHeader>
        <Grid container gap={1} className="w-full flex justify-between">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((data) => handleSubmit(data))}
              className="w-full space-y-2 flex flex-col gap-4"
            >
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem className="max-h-[3.6rem]">
                    <FormLabel>Tipo de problema</FormLabel>
                    <FormControl>
                      <Select
                        {...field}
                        onValueChange={(value) => field.onChange(value)}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Selecciona el tipo de problema" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {types.map((type) => (
                              <SelectItem
                                key={type.value}
                                value={type.value}
                                isSelected={field.value === type.value}
                              >
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="max-h-[10rem]">
                    <FormLabel>Descripción</FormLabel>
                    <FormControl>
                      <Box className="min-w-full max-w-full relative">
                        <Textarea
                          placeholder="Describe tu problema, sugerencia o comentario con el mayor detalle posible..."
                          {...field}
                        />
                      </Box>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="images"
                render={({ field }) => (
                  <FormItem className="max-h-[3.6rem]">
                    <Box className="flex gap-2 justify-between">
                      <FormLabel>Adjuntar imágenes</FormLabel>
                      <Typography
                        fontFamily={"Inter"}
                        fontSize={"12px"}
                        className="text-muted-foreground mt-2 truncate"
                      >
                        {uploadedImages.length} / 2 imágenes adjuntadas
                      </Typography>
                    </Box>
                    <FormControl>
                      <Box className="min-w-full max-w-full relative w-full">
                        <Input
                          type="file"
                          accept="image/*"
                          ref={fileInputRef}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleUploadImage(file);
                              if (fileInputRef.current) {
                                fileInputRef.current.value = "";
                              }
                            }
                          }}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="ml-auto border-gray-200 hover:bg-gray-100 text-black shadow-sm w-full"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadedImages.length >= 2}
                        >
                          <ImagePlus className="w-4 h-4" />
                          {uploadedImages.length >= 2
                            ? "Límite de imágenes alcanzado"
                            : "Seleccionar imagen"}
                        </Button>
                        <Box className="flex w-full pt-2 justify-between">
                          {uploadedImages.map((image) => (
                            <Badge
                              key={image.url}
                              asChild
                              variant="default"
                              className="shadow-sm hover:bg-[#1F6B45] text-[#1F6B45] hover:text-white w-[50%] max-w-[200px]"
                            >
                              <div className="flex gap-2 justify-between">
                                <Typography
                                  fontFamily={"Inter"}
                                  fontSize={"10px"}
                                  fontWeight={400}
                                  textTransform={"capitalize"}
                                  className="max-w-[175px] truncate"
                                >
                                  {image.image.name}
                                </Typography>
                                <span className="rounded-full p-0.5 hover:bg-white/30">
                                  <IconX
                                    className="w-3 h-3 cursor-pointer text-inherit"
                                    onClick={() => handleDeleteImage(image.url)}
                                  />
                                </span>
                              </div>
                            </Badge>
                          ))}
                        </Box>
                      </Box>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter
                className={`flex w-full gap-4 pt-12 flex-row items-center justify-center flex-wrap sm:flex-row sm:justify-end sm:items-center`}
              >
                <Button
                  variant="cancel"
                  className="w-full justify-self-start w-[7.5rem] flex"
                  onClick={() => {
                    resetFormAndImages();
                    setOpen(false);
                  }}
                  disabled={loading}
                >
                  <Typography
                    fontFamily={"Inter"}
                    fontSize={"14px"}
                    fontWeight={500}
                  >
                    Cancelar
                  </Typography>
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  className="w-full justify-self-end w-[7.5rem] flex hover:bg-[#1F6B45]/90"
                  disabled={loading}
                >
                  <Typography
                    fontFamily={"Inter"}
                    fontSize={"14px"}
                    fontWeight={500}
                  >
                    Enviar reporte
                  </Typography>
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default DialogCreateFeedback;
